/**
 * Safe localStorage operations with error handling
 * Prevents crashes from quota exceeded, security errors, etc.
 */

export const safeLocalStorage = {
  /**
   * Safely get an item from localStorage
   */
  getItem<T = string>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue ?? null;
      
      // Try to parse as JSON, otherwise return as string
      try {
        return JSON.parse(item) as T;
      } catch {
        return item as T;
      }
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return defaultValue ?? null;
    }
  },

  /**
   * Safely set an item in localStorage
   */
  setItem<T>(key: string, value: T): boolean {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      if (error instanceof Error) {
        // Quota exceeded error
        if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
          console.error(`localStorage quota exceeded for key: ${key}`);
        } else {
          console.error(`Error writing to localStorage (${key}):`, error);
        }
      }
      return false;
    }
  },

  /**
   * Safely remove an item from localStorage
   */
  removeItem(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
      return false;
    }
  },

  /**
   * Safely clear all localStorage
   */
  clear(): boolean {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  },

  /**
   * Check if localStorage is available
   */
  isAvailable(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get storage usage information
   */
  getStorageInfo(): { used: number; available: boolean } {
    let used = 0;
    try {
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          used += localStorage[key].length + key.length;
        }
      }
    } catch {
      // Ignore errors
    }
    return {
      used: Math.round(used / 1024), // KB
      available: this.isAvailable(),
    };
  },
};

/**
 * Favorites management utilities
 */
export const favoritesStorage = {
  KEY: 'exora-favorites',

  getAll(): string[] {
    return safeLocalStorage.getItem<string[]>(this.KEY, []) ?? [];
  },

  add(hotelId: string): boolean {
    const favorites = this.getAll();
    if (!favorites.includes(hotelId)) {
      favorites.push(hotelId);
      return safeLocalStorage.setItem(this.KEY, favorites);
    }
    return true;
  },

  remove(hotelId: string): boolean {
    const favorites = this.getAll();
    const updated = favorites.filter(id => id !== hotelId);
    return safeLocalStorage.setItem(this.KEY, updated);
  },

  toggle(hotelId: string): { isFavorite: boolean; success: boolean } {
    const favorites = this.getAll();
    const isFavorite = favorites.includes(hotelId);
    
    if (isFavorite) {
      const success = this.remove(hotelId);
      return { isFavorite: false, success };
    } else {
      const success = this.add(hotelId);
      return { isFavorite: true, success };
    }
  },

  has(hotelId: string): boolean {
    return this.getAll().includes(hotelId);
  },

  clear(): boolean {
    return safeLocalStorage.setItem(this.KEY, []);
  },

  count(): number {
    return this.getAll().length;
  },
};

/**
 * Saved hotels management utilities (for SaveButton)
 * Uses consistent key format for unified system
 */
export const savedHotelsStorage = {
  KEY: 'exora:saved_hotels',

  getAll(): string[] {
    return safeLocalStorage.getItem<string[]>(this.KEY, []) ?? [];
  },

  add(hotelId: string): boolean {
    const saved = this.getAll();
    if (!saved.includes(hotelId)) {
      saved.push(hotelId);
      return safeLocalStorage.setItem(this.KEY, saved);
    }
    return true;
  },

  remove(hotelId: string): boolean {
    const saved = this.getAll();
    const updated = saved.filter(id => id !== hotelId);
    return safeLocalStorage.setItem(this.KEY, updated);
  },

  toggle(hotelId: string): { isSaved: boolean; success: boolean } {
    const saved = this.getAll();
    const isSaved = saved.includes(hotelId);
    
    if (isSaved) {
      const success = this.remove(hotelId);
      return { isSaved: false, success };
    } else {
      const success = this.add(hotelId);
      return { isSaved: true, success };
    }
  },

  has(hotelId: string): boolean {
    return this.getAll().includes(hotelId);
  },

  clear(): boolean {
    return safeLocalStorage.setItem(this.KEY, []);
  },
};

/**
 * Recent searches management — stores full Suggestion objects
 */
export type RecentSearchEntry = {
  displayName: string;
  suggestion: {
    type: "country" | "city" | "region" | "hotel";
    name: string;
    regionId?: number;
    hotelId?: string;
    regionType?: string;
    countryCode?: string;
    starRating?: number;
    hotelCount?: number;
    cityName?: string;
  };
};

export const recentSearchesStorage = {
  KEY: 'recentSearches',
  MAX_ITEMS: 5,

  /** Get all recent search entries (full objects), sanitized to drop unusable suggestions */
  getAllEntries(): RecentSearchEntry[] {
    const raw = safeLocalStorage.getItem<any[]>(this.KEY, []) ?? [];
    const entries: RecentSearchEntry[] = raw.map((item: any) => {
      if (typeof item === 'string') {
        return { displayName: item, suggestion: { type: 'city' as const, name: item } };
      }
      return item as RecentSearchEntry;
    });
    // Drop entries whose suggestion can't drive a real search
    // (e.g. legacy string-only entries with no regionId / hotelId).
    const usable = entries.filter((e) => {
      const s = e?.suggestion;
      if (!s || !s.type || !s.name) return false;
      if (s.type === 'hotel') return !!s.hotelId;
      if (s.type === 'country') return !!(s.regionId || s.countryCode);
      // city / region
      return s.regionId != null;
    });
    // If we cleaned anything up, persist the cleaned version so it stays clean.
    if (usable.length !== entries.length) {
      safeLocalStorage.setItem(this.KEY, usable);
    }
    return usable;
  },

  /** Get display names only (backward compat) */
  getAll(): string[] {
    return this.getAllEntries().map(e => e.displayName);
  },

  /** Add a recent search with full suggestion data */
  addEntry(displayName: string, suggestion: RecentSearchEntry['suggestion']): boolean {
    const entries = this.getAllEntries();
    const entry: RecentSearchEntry = { displayName, suggestion };
    const updated = [entry, ...entries.filter(e => e.displayName !== displayName)].slice(0, this.MAX_ITEMS);
    return safeLocalStorage.setItem(this.KEY, updated);
  },

  /** Legacy add — wraps as city type */
  add(searchTerm: string): boolean {
    return this.addEntry(searchTerm, { type: 'city', name: searchTerm });
  },

  /** Remove a single recent entry by displayName. Returns true if removed. */
  removeEntry(displayName: string): boolean {
    const entries = this.getAllEntries();
    const updated = entries.filter((e) => e.displayName !== displayName);
    if (updated.length === entries.length) return false;
    return safeLocalStorage.setItem(this.KEY, updated);
  },

  clear(): boolean {
    return safeLocalStorage.removeItem(this.KEY);
  },
};
